import type { TAddressQPV } from "@bbj-types/address";
import type { IManagementProcess } from "@bbj-types/applyManagementProcess";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISettingValueMaster } from "@bbj-types/setting";
import type { Override } from "@bbj-types/utils";

export const formatTimeFromDate = (
  date: Date | undefined,
  options: { prefix?: string } | undefined,
  timeZone: string
): string => {
  if (date && (date.getHours() !== 0 || date.getMinutes() !== 0)) {
    const formattedTime = date
      .toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone,
      })
      .replace(":", "h");

    return `${options?.prefix ?? ""} ${formattedTime}`;
  }
  return "";
};

export const getSettingsCoachingStepLabel = (item: { coachingStep: string }, settings: ISettingValueMaster[]) => {
  const value = item.coachingStep;
  if (settings?.length) {
    const coachingStep = settings.find((s) => s.value === value);
    return coachingStep?.label ? coachingStep.label : value;
  }
  return value;
};

export const getSettingsStatusLabel = (status: string, settings: ISettingValueMaster[]) => {
  if (status === "hired_PE") {
    const labelStatus = "Embauché(e) avec France Travail";
    return labelStatus;
  }
  if (settings?.length) {
    const newStatus = settings.find((s) => s.value === status);
    return newStatus?.label ? newStatus.label : status;
  }
  return status;
};

export const getSettingsLabelFromValue = (
  value: string,
  settings: Pick<ISettingValueMaster, "value" | "label" | "active">[] | undefined,
  options?: { withDeactivatedSuffix?: boolean }
): string => {
  const item = settings?.find((obj) => obj.value === value);
  if (!item) {
    return "";
  }
  if (options?.withDeactivatedSuffix && item.active === false) {
    return `${item.label} (désactivé)`;
  }
  return item.label;
};
export const getVehiclesLabel = (value: "no" | "yes") => {
  let vehicleValue: "non" | "oui" | undefined = undefined;
  switch (value) {
    case "no":
      vehicleValue = "non";
      break;
    case "yes":
      vehicleValue = "oui";
      break;
    default:
      break;
  }
  return vehicleValue;
};

export const getFormationLevelLabel = (EQFlevel: number) => {
  switch (EQFlevel) {
    case 3:
      return "CAP";
    case 4:
      return "Bac";
    case 5:
      return "Bac+2";
    case 6:
      return "Bac+3";
    case 7:
      return "Bac+5";
    case 8:
      return "Bac+8";
  }
};

export const removeDuplicateFromArray = <
  T extends
    | {
        _id?: string;
      }
    | string,
>(
  arr: T[]
): T[] => {
  const seenArray = new Set<string>();

  const arrTemp = arr.filter((item) => {
    let itemToCheck = "";

    if (typeof item === "object") {
      itemToCheck = item?._id ? `${item._id}` : JSON.stringify(item);
    } else if (typeof item === "string") {
      itemToCheck = `${item}`;
    }

    return seenArray.has(itemToCheck) ? false : seenArray.add(itemToCheck);
  });

  return arrTemp;
};

export const formatTime = (time: number) => {
  if (time > 0) {
    const hours = Math.floor(time / 60) > 0 ? `${Math.floor(time / 60)}h` : undefined;
    const minutes = time % 60 > 0 ? `${time % 60}min` : undefined;

    return [hours, minutes].filter(Boolean).join(" ");
  }
  return undefined;
};

export const getStatusLabelFromAPM = (apm: IManagementProcess, status: string) => {
  if (apm?.pipes.length && status) {
    const statusList: { key: string; label: string }[] = [];
    for (const pipe of apm.pipes) {
      for (const { key, name } of pipe.status) {
        statusList.push({ key: key, label: name });
      }
    }
    const statusFound = statusList.find(({ key }) => key === status);

    return statusFound ? statusFound.label : "";
  }

  return "";
};

export const formatAddressToDisplay = (address: TAddressQPV, format: string, addressComplement) => {
  if (address?.city || address?.postalCode) {
    const { city, postalCode, formatted, isQPV } = address;
    let stringBuilder = "";
    switch (format) {
      case "city (postalCode)":
        stringBuilder = city || "";
        if (postalCode) {
          stringBuilder = `${stringBuilder} (${postalCode})`;
        }
        break;
      case "postalCode - City":
        stringBuilder = city || "";
        if (postalCode) {
          stringBuilder = `${postalCode}, ${stringBuilder} `;
        }
        break;
      case "address":
        if (formatted) {
          stringBuilder = `${formatted} ${isQPV ? "(QPV)" : ""}${addressComplement ? `, ${addressComplement}` : ""}`;
        }
        break;
    }
    return stringBuilder;
  }
};

export const formatExperienceTotal = (experience, by) => {
  switch (by) {
    case "byDays":
      if (Math.floor(experience / 365) > 1) {
        return `${Math.floor(experience / 365)} ans`;
      }
      if (Math.floor(experience / 365) === 1) {
        return "1 an";
      }
      if (experience > 30) {
        return `${Math.floor(experience / 30)} mois`;
      }
      if (experience < 30 && experience > 1) {
        return `${experience} jours`;
      }
      if (experience === 1) {
        return "1 jour";
      }
      return "";

    default:
      break;
  }
};

export const formatJobTitle = (
  job: { maleLibelle?: string; femaleLibelle?: string; libelleCourt?: string; libelle: string },
  candidateGender?: ICandidate["gender"]
) => {
  if (job) {
    if (candidateGender === "M" && job.maleLibelle) {
      return job.maleLibelle;
    }
    if (candidateGender === "F" && job.femaleLibelle) {
      return job.femaleLibelle;
    }
    return job.libelleCourt ?? job.libelle;
  }
  return "";
};

export const urlToDomain = (url: string) => {
  const reg = /^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n]+)/im;
  if (!url || reg.exec(url) == null) {
    return "";
  }
  return reg.exec(url)?.[1];
};

export const verifyCandidatesToCreateCandidates = (verifyCandidates: ICandidate[]) => {
  const candidatesToCreate: Override<ICandidate, { authors: string[] }>[] = [];

  if (verifyCandidates) {
    verifyCandidates.map((candidate) => {
      const authorsIds: string[] = [];
      if (candidate.authors) {
        candidate.authors.map((author) => {
          authorsIds.push(author._id);
        });
      }
      candidatesToCreate.push({ ...candidate, authors: authorsIds });
    });
  }
  return candidatesToCreate;
};
